import React, { useState, useEffect, useMemo } from 'react';
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import SparkleDust from './SparkleDust';

interface ShopProps {
  onBack: () => void;
  onBuyTokens: (amount: number) => void;
  tokens: number;
  userId: string;
}

const PACKAGES = [
  {
    id: 'starter',
    name: 'Thesari i Vogël',
    tokens: 5,
    price: '2.99',
    displayPrice: '2.99€',
    description: 'Ideale për fillimin e udhëtimit magjik.',
    icon: <span className="text-5xl">⚡</span>,
    color: 'from-blue-500/20 to-indigo-500/20',
    borderColor: 'border-blue-500/30',
  },
  {
    id: 'popular',
    name: 'Kutia e Magjisë',
    tokens: 15,
    price: '7.99',
    displayPrice: '7.99€',
    description: 'Më e pëlqyera nga prindërit tregimtarë.',
    icon: <span className="text-5xl">✨</span>,
    color: 'from-purple-500/30 to-pink-500/30',
    borderColor: 'border-purple-400/50',
    popular: true,
  },
  {
    id: 'legendary',
    name: 'Mbretëria e Përrallave',
    tokens: 50,
    price: '19.99',
    displayPrice: '19.99€',
    description: 'Për tregime të pafundme...',
    icon: <span className="text-5xl">👑</span>,
    color: 'from-yellow-500/20 to-orange-500/20',
    borderColor: 'border-yellow-400/30',
  }
];

const PayPalPackageButton: React.FC<{ pkg: typeof PACKAGES[0]; userId: string; onComplete: (tokens: number) => void; onError: (msg: string) => void }> = ({ pkg, userId, onComplete, onError }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [{ isPending, isResolved, isRejected }] = usePayPalScriptReducer();

  useEffect(() => {
    if (isRejected) {
      console.error("[PayPal] Script failed to load. Check Client ID and Network.");
    }
    if (isResolved) {
      console.log("[PayPal] Script loaded successfully.");
    }
  }, [isResolved, isRejected]);

  const handleCreateOrder = async () => {
    try {
      console.log(`Nisja e porosisë për ${pkg.name}...`);
      const response = await fetch("/api/paypal/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart: [{ price: pkg.price, userId }]
        }),
      });
      
      const order = await response.json();
      if (!response.ok || !order.id) {
        throw new Error(order.error || order.message || "Dështoi krijimi i porosisë në server");
      }
      return order.id;
    } catch (err: any) {
      console.error("PayPal Create Order Error:", err);
      onError(err.message || "Nuk u krijua dot porosia. Kontrolloni lidhjen.");
      throw err;
    }
  };

  const handleApprove = async (data: any) => {
    setIsProcessing(true);
    try {
      const response = await fetch("/api/paypal/capture-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderID: data.orderID }),
      });
      const orderData = await response.json();
      
      if (!response.ok) {
        let errorMsg = orderData.error || orderData.message || "Procesimi i pagesës dështoi";
        
        // Check for specific compliance violation
        if (orderData.issue === 'COMPLIANCE_VIOLATION' || (orderData.details && orderData.details.some((d: any) => d.issue === 'COMPLIANCE_VIOLATION'))) {
          errorMsg = "Transaksioni u refuzua nga PayPal për arsye sigurie. Kjo ndodh zakonisht nëse llogaria juaj nuk është e verifikuar ose nëse po provoni të paguani veten tuaj. Ju lutem përdorni një llogari tjetër.";
        }
        
        throw new Error(errorMsg);
      }
      
      if (orderData.status === 'COMPLETED') {
        onComplete(pkg.tokens);
      } else {
        throw new Error(`Statusi: ${orderData.status}. Kontaktoni mbështetjen.`);
      }
    } catch (err: any) {
      console.error("PayPal Capture Error:", err);
      onError(err.message || "Procesimi i pagesës dështoi.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full mt-auto space-y-3 min-h-[150px] flex flex-col justify-center">
      {isPending && (
        <div className="flex flex-col items-center gap-3 py-8">
          <div className="w-8 h-8 border-4 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin"></div>
          <p className="text-[10px] text-indigo-200 font-bold uppercase tracking-[0.2em]">Duke ngarkuar PayPal...</p>
        </div>
      )}
      
      {isRejected && (
        <div className="text-center py-6 px-4 bg-red-500/10 border border-red-500/20 rounded-3xl space-y-3">
          <p className="text-sm text-red-300 font-medium">Dështoi ngarkimi i PayPal.</p>
          <div className="space-y-1">
            <p className="text-[10px] text-red-400/60 break-all px-4">
              ID: {import.meta.env.VITE_PAYPAL_CLIENT_ID ? `${import.meta.env.VITE_PAYPAL_CLIENT_ID.substring(0, 12)}...` : 'Mungon (Fallback)'}
            </p>
            <p className="text-[9px] text-indigo-300/40 uppercase tracking-widest">
              Mjedisi: {import.meta.env.VITE_PAYPAL_MODE || 'sandbox'}
            </p>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <button 
              onClick={() => window.location.reload()}
              className="text-[10px] bg-red-500/20 hover:bg-red-500/40 text-red-200 px-4 py-2 rounded-full uppercase tracking-widest transition-all"
            >
              Rifresko Faqen
            </button>
            <p className="text-[9px] text-red-400/40 italic">
              Sigurohuni që ID-ja përputhet me mjedisin (Live/Sandbox) në PayPal Dashboard.
            </p>
          </div>
        </div>
      )}

      {(isResolved || !isPending) && (
        <PayPalButtons
          style={{ 
            layout: 'vertical', 
            color: 'gold', 
            shape: 'pill',
            label: 'pay'
          }}
          disabled={isProcessing}
          createOrder={handleCreateOrder}
          onApprove={handleApprove}
          onError={(err) => {
            console.error("PayPal SDK Error:", err);
            onError("Gabim teknik me PayPal. Sigurohuni që nuk keni AdBlock aktiv.");
          }}
        />
      )}
      
      {isProcessing && (
        <div className="flex flex-col items-center gap-2 mt-2">
          <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[10px] text-yellow-400 font-bold uppercase tracking-widest">Duke procesuar...</p>
        </div>
      )}
    </div>
  );
};

const Shop: React.FC<ShopProps> = ({ onBack, onBuyTokens, tokens, userId }) => {
  const [error, setError] = useState<string | null>(null);
  const isLive = import.meta.env.VITE_PAYPAL_MODE === 'live';
  
  useEffect(() => {
    const cid = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    console.log(`[PayPal] Mode: ${isLive ? 'LIVE' : 'SANDBOX'}`);
    console.log(`[PayPal] Client ID: ${cid ? cid.substring(0, 5) + '...' : 'FALLBACK'}`);
  }, [isLive]);

  const initialOptions = useMemo(() => {
    const cid = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    const finalId = (cid && cid.trim().length > 0) ? cid.trim() : "";

    return {
      clientId: finalId,
      currency: "EUR",
      intent: "capture",
      components: "buttons",
      locale: "en_AL",
    };
  }, []);

  return (
    <PayPalScriptProvider options={initialOptions} key={initialOptions.clientId}>
      <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 max-w-5xl mx-auto w-full px-4 pb-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-6xl font-bold text-white font-fairytale mb-4 text-glow-yellow">Dyqani Magjik</h2>
          <div className="flex flex-col items-center gap-2">
            <p className="text-indigo-200/70 max-w-lg mx-auto italic">
              Zgjidhni një paketë xhevahiresh për të vazhduar krijimin e përrallave tuaja unike.
            </p>
            <span className={`text-[10px] px-3 py-1 rounded-full font-black uppercase tracking-widest ${isLive ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'}`}>
              {isLive ? '● Live Mode' : '○ Test Mode (Sandbox)'}
            </span>
          </div>
          {error && (
            <div className="mt-6 p-4 bg-red-500/20 border border-red-500/50 rounded-2xl text-red-200 text-sm animate-in fade-in zoom-in">
              <p className="font-bold">Gabim: {error}</p>
              <button onClick={() => setError(null)} className="mt-2 text-xs underline opacity-70 hover:opacity-100">Provo përsëri</button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PACKAGES.map((pkg) => (
            <div 
              key={pkg.id}
              className={`glass-card rounded-[2.5rem] p-8 border ${pkg.borderColor} bg-gradient-to-br ${pkg.color} relative overflow-hidden flex flex-col transition-all hover:scale-[1.02] hover:shadow-2xl group`}
            >
              {pkg.popular && (
                <div className="absolute top-0 right-0 bg-yellow-400 text-indigo-950 text-[10px] font-black px-6 py-1.5 rounded-bl-2xl uppercase tracking-widest shadow-lg z-10">
                  Më i Shitur
                </div>
              )}

              <div className="mb-6 flex justify-center">
                <div className="p-5 bg-white/10 rounded-3xl group-hover:rotate-6 transition-transform duration-500">
                  {pkg.icon}
                </div>
              </div>

              <h3 className="text-2xl font-bold text-white text-center mb-2 font-comic">{pkg.name}</h3>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-4xl font-bold text-yellow-400 font-fairytale">{pkg.tokens}</span>
                <span className="text-2xl">💎</span>
              </div>

              <p className="text-indigo-100/60 text-center text-sm mb-8 italic leading-relaxed">
                {pkg.description}
              </p>

              <div className="text-center mb-6">
                <span className="text-3xl font-bold text-white font-fairytale">{pkg.displayPrice}</span>
              </div>

              <PayPalPackageButton 
                pkg={pkg}
                userId={userId} 
                onComplete={onBuyTokens}
                onError={setError}
              />
            </div>
          ))}
        </div>

        <div className="mt-16 p-10 glass-card rounded-[3rem] border border-white/5 bg-white/5 text-center relative overflow-hidden">
          <SparkleDust />
          <p className="text-indigo-200/40 text-[10px] uppercase tracking-[0.4em] mb-4 font-black">Siguria Magjike</p>
          <p className="text-indigo-100/60 text-sm italic max-w-2xl mx-auto leading-relaxed">
            Të gjitha transaksionet kryhen përmes PayPal për siguri maksimale. 
            Nëse keni pyetje, na shkruani në <span className="text-yellow-400 font-bold">artemontamo@me.com</span>
          </p>
        </div>

        <button 
          onClick={onBack}
          className="mt-12 block mx-auto text-indigo-300/40 hover:text-white font-black uppercase text-[10px] tracking-[0.5em] transition-all hover:tracking-[0.6em]"
        >
          ← Kthehu te përrallat
        </button>
      </div>
    </PayPalScriptProvider>
  );
};

export default Shop;
